<script lang="ts" setup>
import InvestorHero from '@/domains/investor/components/Hero/Hero.vue'
import FileList from '@/domains/investor/components/FileList/FileList.vue'
import { BsTabs } from '@afo/bsf-core'
import { EPresentationCategories } from '@/domains/investor/const/PresentationCategories.enum'
import type { IInvestorPresentationTab } from '@/domains/investor/types/investor.types'
import { usePresentations } from '@/domains/investor/composables/usePresentations'

definePageMeta({
  layout: 'investor',
})

const { t, locale } = useI18n()

useHead(() => ({
  title: t('pageTitles.investorPresentations.title'),
  meta: [
    {
      name: 'description',
      content: t('pageTitles.investorPresentations.meta'),
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: t(`pageTitles.investorPresentations.canonical.${locale.value}`),
    },
    {
      rel: 'alternate',
      hreflang: 'pl',
      href: t('pageTitles.investorPresentations.canonical.pl'),
    },
    {
      rel: 'alternate',
      hreflang: 'en',
      href: t('pageTitles.investorPresentations.canonical.en'),
    },
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: t('pageTitles.investorPresentations.canonical.pl'),
    },
  ],
}))

const {
  tabs,
  selectedTab,
  mainCategory,
  secondCategory,
  changeTab,
  isTabActivated,
  presentationRequestId,
  strategyRequestId,
  otherRequestId,
} = usePresentations()
</script>

<template>
  <div class="investor-presentations-page">
    <InvestorHero
      class="investor-presentations-page__hero"
      :title="$t('investor.stock.presentations.title')"
      :lead="$t('investor.stock.presentations.description')"
      :main-category="mainCategory"
      :second-category="secondCategory"
      image="/images/investor/presentations/presentations.png"
      :first-button="{ text: $t('investor.stock.presentations.firstButton'), to: '#investor-presentations-page__presentations' }"
    />

    <UiSection
      id="investor-presentations-page__presentations"
      class="investor-presentations"
    >

      <UiHeader
        :header="$t('investor.stock.presentations.reports.title')"
        position="center"
        header-size="h5"
        header-size-tablet="h5"
        header-size-mobile="h5"
      />

      <div class="section__container">
        <div class="investor-presentations__results">
          <h3 class="investor-presentations__results-title">
            {{ $t('investor.stock.presentations.reports.description') }}
          </h3>

          <ClientOnly>
            <BsTabs
              class="investor-presentations__tabs"
              :tabs="tabs"
              :model-value="selectedTab"
              :size="'large'"
              @update:model-value="changeTab($event as IInvestorPresentationTab)"
            >
              <template #Prezentacja>
                <FileList
                  v-if="isTabActivated(EPresentationCategories.PRESENTATION)"
                  v-show="selectedTab.slotName === EPresentationCategories.PRESENTATION"
                  :request-id="presentationRequestId"
                  :show-title="false"
                  :file-item-options="{
                    showDate: true,
                  }"
                  :file-item-link-options="{label:t('investor.files.seePresentation')}"
                />
              </template>

              <template #Strategia>
                <FileList
                  v-if="isTabActivated(EPresentationCategories.STRATEGY)"
                  v-show="selectedTab.slotName === EPresentationCategories.STRATEGY"
                  :request-id="strategyRequestId"
                  :show-title="false"
                  :file-item-options="{
                    showDate: true,
                  }"
                  :file-item-link-options="{label:t('investor.files.seePresentation')}"
                />
              </template>

              <template #Inne>
                <FileList
                  v-if="isTabActivated(EPresentationCategories.OTHER)"
                  v-show="selectedTab.slotName === EPresentationCategories.OTHER"
                  :request-id="otherRequestId"
                  :show-title="false"
                  :file-item-options="{
                    showDate: true,
                  }"
                  :file-item-link-options="{label:t('investor.files.seePresentation')}"
                />
              </template>
            </BsTabs>
          </ClientOnly>
        </div>
      </div>
    </UiSection>
  </div>
</template>

<style lang="scss" scoped>
.investor-presentations {
  display: grid;

  :deep(.bs-tabs__list) {
    margin-bottom: 2.4rem;
  }

  :deep(.bs-tabs__button) {
    @include body-3-medium;
    margin-right: 0.8rem;
    padding: 1rem 1.6rem;
    border: 1px solid $gray-400;
    border-radius: 4.8rem;
  }

  :deep(.bs-tabs__button--selected) {
    border: 1px solid $text-400;
  }

  &__results {
    display: grid;  }

  &__results-title {
    @include h9;
    margin-bottom: 0.8rem;
    color: $text-400;
  }

  &__tabs {
    margin-bottom: 0;
  }
}
</style>
